import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnInit } from '@angular/core';
import {
  buildCancelInsuranceRequest,
  CANCELLATION_REASONS,
  CANCELLED_CONTRACT_STATUS,
  CancellationReason,
  CancellationType,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  isPetReason,
  PetInsuranceData
} from "../../../models";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { DialogService } from "../../../services/dialog.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import moment from 'moment';
import { firstValueFrom } from "rxjs";
import { ApiHttpService } from "../../../services/api-http.service";

const MODALS = 'cancel-insurance-reason.modals';

@Component({
  selector: 'cpf-cancel-insurance-reason',
  templateUrl: './cancel-insurance-reason.component.html',
  styleUrls: ['./cancel-insurance-reason.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CancelInsuranceReasonComponent implements OnInit {
  @Input() petData!: PetInsuranceData;

  @Output()
  close: EventEmitter<void> = new EventEmitter<void>();

  readonly reasons = CANCELLATION_REASONS;
  readonly isPetReason = isPetReason;

  selectedReason: CancellationReason | '' = '';
  petDeceasedDate: moment.Moment | undefined;
  cancellationType: CancellationType = 'end-of-insurance-year';
  cancellationDate: moment.Moment | undefined; // used when 'end-of-month' cancellation type selected
  today: Date = new Date();
  /** The request is on its way; the submit button waits and shows a spinner. */
  sending = false;

  constructor(private translate: TranslateService,
              private router: Router,
              private dialogService: DialogService,
              private apiHttpService: ApiHttpService,) {
  }

  ngOnInit(): void {
    // CI shows the contract as cancelled already: there is nothing left to cancel. Right
    // after a cancellation CI still shows it active, until customer service processes it.
    if (this.petData?.status === CANCELLED_CONTRACT_STATUS) {
      this.leave();
    }
  }

  cancellationDateFilter = (date: moment.Moment | null): boolean => {
    if (!date) return false;

    const today = moment().startOf('day');

    if (date.isBefore(today)) {
      return false;
    }

    const lastDayOfMonth = moment(date).endOf('month').date();
    return date.date() === lastDayOfMonth;
  };

  submitButtonDisabled(): boolean {
    if (this.sending || this.selectedReason === '') {
      return true;
    }
    if (this.selectedReason === 'pet-deceased') {
      return !this.petDeceasedDate;
    }
    if (this.selectedReason === 'pet-missing') {
      return false;
    }
    return this.cancellationType === 'end-of-month' && !this.cancellationDate;
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'insurance', 'details'])
  }

  /**
   * Asks where the form asks, sends, and thanks the customer only once customer service
   * has the cancellation. A failed send keeps the customer on the form.
   */
  async cancelInsurance() {
    if (this.submitButtonDisabled()) {
      return;
    }
    const reason = this.selectedReason as CancellationReason;

    if (reason === 'pet-missing' && !await this.confirm('missing-confirmation')) {
      return;
    }
    if (!isPetReason(reason) && !await this.confirm('default', true)) {
      return;
    }

    if (!await this.send(reason)) {
      return;
    }

    await this.showThanks(reason);
    this.leave();
  }

  private async send(reason: CancellationReason): Promise<boolean> {
    const request = buildCancelInsuranceRequest(this.petData.full_contract_number, {
      reason,
      type: this.cancellationType,
      cancellationDate: this.cancellationDate,
      petDeceasedDate: this.petDeceasedDate,
    });

    this.sending = true;
    try {
      await this.apiHttpService.cancelInsurance(request);
      return true;
    } catch (e) {
      console.error('Cancellation was not sent', e);
      const texts = await this.texts([`${MODALS}.error.title`, `${MODALS}.error.message`]);
      await this.openModal({
        title: texts[`${MODALS}.error.title`],
        message: texts[`${MODALS}.error.message`],
        confirmButton: GeneralConfirmationModalResult.Confirm,
      });
      return false;
    } finally {
      this.sending = false;
    }
  }

  private async confirm(modal: 'default' | 'missing-confirmation', useInnerHtml = false): Promise<boolean> {
    const texts = await this.texts([`${MODALS}.${modal}.title`, `${MODALS}.${modal}.message`]);
    const result = await this.openModal({
      title: texts[`${MODALS}.${modal}.title`],
      message: texts[`${MODALS}.${modal}.message`],
      confirmButton: GeneralConfirmationModalResult.Confirm,
      cancelButton: GeneralConfirmationModalResult.Cancel,
      useInnerHtml,
    });
    return result === GeneralConfirmationModalResult.Confirm;
  }

  private async showThanks(reason: CancellationReason): Promise<void> {
    if (reason === 'pet-deceased') {
      const texts = await this.texts([`${MODALS}.deceased-final.title`, `${MODALS}.deceased-final.message`]);
      await this.openModal({
        title: texts[`${MODALS}.deceased-final.title`],
        message: texts[`${MODALS}.deceased-final.message`],
        confirmButton: GeneralConfirmationModalResult.Confirm,
      });
      return;
    }

    if (reason === 'pet-missing') {
      const keys = [`${MODALS}.missing-final.title`, `${MODALS}.missing-final.message`, `${MODALS}.missing-final.main-button`];
      const texts = await this.texts(keys, { petName: this.petData.pet_name });
      await this.openModal({
        title: texts[keys[0]],
        message: texts[keys[1]],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: texts[keys[2]],
      });
      return;
    }

    const texts = await this.texts([`${MODALS}.final.title`]);
    await this.openModal({
      title: texts[`${MODALS}.final.title`],
      isImage: true,
      imageSrc: '/assets/cancel-contact-image.png',
      confirmButton: GeneralConfirmationModalResult.Confirm,
    });
  }

  /** Back to the contract: the desktop side modal closes, the mobile page goes to the details. */
  private leave() {
    if (this.close.observed) {
      this.close.emit();
      return;
    }

    this.router.navigate(['calingo', 'pet', 'insurance', 'details']);
  }

  private texts(keys: string[], params?: object): Promise<Record<string, string>> {
    return firstValueFrom(this.translate.get(keys, params));
  }

  private openModal(data: Partial<GeneralConfirmationModalData>): Promise<GeneralConfirmationModalResult | undefined> {
    const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, { data });
    return firstValueFrom(dialogRef.afterClosed());
  }
}
